import { useRef, useState } from 'react';
import { isPlatformApiError } from '@traceio-org/platform-client';
import {
  STAFF_ROLES,
  type StaffInvitation,
  type StaffMember,
  type StaffRole,
} from '@traceio-org/platform-contracts';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { AdminPage } from '../components/AdminPage';
import { AdminProblem } from '../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../components/fields';
import { DateText, ScrollTable, StatusBadge } from '../components/records';
import { WriteDialog, useDialogTarget } from '../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../context';
import { STAFF_ROLE_LABELS } from '../format';
import { useAdminRead } from '../useAdminRead';

const ROLE_OPTIONS = STAFF_ROLES.map((role) => ({ value: role, label: STAFF_ROLE_LABELS[role] }));

const FIND_BY = [
  {
    value: 'email',
    label: 'Email',
    description: 'Their work email. If they have never signed in to Trace, they get an invitation by email.',
  },
  {
    value: 'subject',
    label: 'Sign-in ID',
    description: 'Their Auth0 user ID, for example google-oauth2|1234. Use it when an email is shared.',
  },
] as const;

const STATUS_CHOICES = [
  { value: 'active', label: 'Active', description: 'Signs in to Trace Admin with this role.' },
  { value: 'suspended', label: 'Suspended', description: 'Loses Trace Admin until reactivated.' },
  { value: 'revoked', label: 'Revoked', description: 'No longer Trace staff.' },
] as const;

type Dialog =
  { kind: 'add' } | { kind: 'change'; member: StaffMember } | { kind: 'revoke'; invitation: StaffInvitation };

interface Form {
  findBy: 'email' | 'subject';
  person: string;
  staffRole: StaffRole | '';
  status: StaffMember['status'];
  reason: string;
}

type Errors = Partial<Record<'person' | 'staffRole' | 'reason', string>>;

const EMPTY: Form = { findBy: 'email', person: '', staffRole: '', status: 'active', reason: '' };

/** True for platform-api's answer that it knows nobody with that sign-in or email (POST /v1/admin/staff). */
function isPersonNotFound(error: unknown): boolean {
  return isPlatformApiError(error) && error.status === 404 && error.problem.reason === 'person_not_found';
}

/**
 * Trace staff (platform admins only). Add staff by email grants the role at once to a person Trace already knows; for
 * anyone else (they never signed in to Trace, so there is no one to grant it to) it sends a staff invitation, and they
 * become staff when they accept it signed in with that address. By sign-in ID it only finds a known person. Nothing
 * typed here creates an identity: platform-api links one only from a verified sign-in. Platform
 * admins change roles and suspend or revoke staff, never their own row, and never the last platform admin;
 * platform-api decides all of it again (POST /v1/admin/staff, PATCH /v1/admin/staff/:userId).
 */
export function StaffPage() {
  const { api } = useAdmin();
  const { person: me } = useStaff();
  const abilities = useAbilities();
  const key = abilities.manageStaff ? 'staff' : null;
  const read = useAdminRead(key, (signal) => api.listStaff(signal));
  const staff = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const invitationsKey = abilities.manageStaff ? 'staff-invitations' : null;
  const invitationsRead = useAdminRead(invitationsKey, (signal) => api.listStaffInvitations(signal));
  const invitations =
    invitationsRead.dataKey === invitationsKey && !invitationsRead.failure
      ? (invitationsRead.data?.items ?? null)
      : null;
  // What the last Add staff did (granted now, or invited by email), for the notice once the dialog closes.
  const added = useRef<'granted' | 'invited'>('granted');

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<Dialog>();
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const set = <K extends keyof Form>(field: K, value: Form[K]) =>
    setForm((current) => ({ ...current, [field]: value }));
  const open = (target: Dialog) => {
    setForm(
      target.kind === 'change'
        ? { ...EMPTY, staffRole: target.member.staffRole, status: target.member.status }
        : target.kind === 'revoke'
          ? { ...EMPTY, staffRole: target.invitation.staffRole }
          : EMPTY,
    );
    setErrors({});
    dialog.show(target);
  };
  const target = dialog.target;

  let body;
  if (!abilities.manageStaff) {
    body = <p className="admin-muted">Only platform admins see and change Trace staff.</p>;
  } else if (read.failure) {
    body = <AdminProblem failure={read.failure} subject="the staff" onRetry={read.retry} />;
  } else if (!staff) {
    body = <LoadingBlock label="Loading the staff" />;
  } else {
    body = (
      <ScrollTable caption="Trace staff" className="admin-table" busy={read.loading}>
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">Sign-ins</th>
            <th scope="col">Granted</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {staff.map((member) => (
            <tr key={member.userId}>
              <th scope="row">
                {member.displayName}
                {member.email ? <div className="admin-muted">{member.email}</div> : null}
              </th>
              <td>{STAFF_ROLE_LABELS[member.staffRole]}</td>
              <td>
                <StatusBadge status={member.status} />
              </td>
              <td>
                {member.subjects.length === 0 ? (
                  <span className="admin-muted">None</span>
                ) : (
                  <ul className="admin-plain-list">
                    {member.subjects.map((subject) => (
                      <li key={subject}>
                        <code className="admin-id">{subject}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td>
                <DateText iso={member.grantedAt} />
                {member.grantedByName ? <div className="admin-muted">by {member.grantedByName}</div> : null}
              </td>
              <td>
                {member.userId === me.userId ? (
                  <span className="admin-muted">You</span>
                ) : (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => open({ kind: 'change', member })}
                  >
                    Change
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );
  }

  let invitationsBody;
  if (!abilities.manageStaff) {
    invitationsBody = null;
  } else if (invitationsRead.failure) {
    invitationsBody = (
      <AdminProblem
        failure={invitationsRead.failure}
        subject="the staff invitations"
        onRetry={invitationsRead.retry}
      />
    );
  } else if (!invitations) {
    invitationsBody = <LoadingBlock label="Loading the staff invitations" />;
  } else if (invitations.length === 0) {
    invitationsBody = <p className="admin-muted">No invitation is waiting to be accepted.</p>;
  } else {
    invitationsBody = (
      <ScrollTable caption="Staff invitations" className="admin-table" busy={invitationsRead.loading}>
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">Sent</th>
            <th scope="col">Expires</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation) => (
            <tr key={invitation.id}>
              <th scope="row">{invitation.emailHint}</th>
              <td>{STAFF_ROLE_LABELS[invitation.staffRole]}</td>
              <td>
                <StatusBadge status={invitation.status} />
              </td>
              <td>
                <DateText iso={invitation.createdAt} />
                {invitation.createdByName ? (
                  <div className="admin-muted">by {invitation.createdByName}</div>
                ) : null}
              </td>
              <td>
                <DateText iso={invitation.expiresAt} />
              </td>
              <td>
                {invitation.status === 'pending' ? (
                  <button
                    type="button"
                    className="admin-action"
                    onClick={() => open({ kind: 'revoke', invitation })}
                  >
                    Revoke
                  </button>
                ) : (
                  <span className="admin-muted">Add them again to send a new one</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );
  }

  return (
    <AdminPage title="Staff">
      <PageHeading
        title="Staff"
        description="Who signs in to Trace Admin, and with which role. Every change is recorded in the audit log."
      />
      <SavedNotice message={notice} />
      <PageSection
        title="Trace staff"
        description="Add someone by email. If they have never signed in to Trace, they get an invitation and become staff when they accept it."
        action={
          abilities.manageStaff && staff ? (
            <button
              type="button"
              className="admin-action admin-action--primary"
              onClick={() => open({ kind: 'add' })}
            >
              Add staff
            </button>
          ) : undefined
        }
      >
        {body}
      </PageSection>
      {abilities.manageStaff ? (
        <PageSection
          title="Invitations"
          description="Invitations to become Trace staff that are not accepted yet. A link works once, for the address it was sent to."
        >
          {invitationsBody}
        </PageSection>
      ) : null}
      <WriteDialog
        key={dialog.key}
        open={dialog.open}
        onClose={dialog.close}
        title={
          target?.kind === 'change'
            ? `Change ${target.member.displayName}`
            : target?.kind === 'revoke'
              ? `Revoke the invitation for ${target.invitation.emailHint}?`
              : 'Add staff'
        }
        description={
          target?.kind === 'change'
            ? 'Takes effect on their next request in Trace Admin.'
            : target?.kind === 'revoke'
              ? 'Its link stops working at once. You can invite the same person again later.'
              : 'Someone Trace already knows gets Trace Admin on their next sign-in; anyone else gets an invitation by email. Staff sign in with a second factor.'
        }
        submitLabel={
          target?.kind === 'change'
            ? 'Save the change'
            : target?.kind === 'revoke'
              ? 'Revoke the invitation'
              : 'Add staff'
        }
        tone={target?.kind === 'revoke' ? 'danger' : 'default'}
        onSubmit={async () => {
          if (target?.kind === 'revoke') {
            const reason = reasonError(form.reason);
            setErrors(reason ? { reason } : {});
            if (reason) return 'invalid';
            await api.revokeStaffInvitation(target.invitation.id, form.reason);
            return undefined;
          }
          const found: Errors = {};
          if (target?.kind !== 'change' && !form.person.trim()) {
            found.person = form.findBy === 'email' ? 'Enter their email.' : 'Enter their sign-in ID.';
          }
          if (!form.staffRole) found.staffRole = 'Choose a role.';
          const reason = reasonError(form.reason);
          if (reason) found.reason = reason;
          setErrors(found);
          if (Object.keys(found).length > 0) return 'invalid';
          const staffRole = form.staffRole as StaffRole;
          if (target?.kind === 'change') {
            // Both are sent; platform-api changes nothing (and records nothing) when neither differs.
            await api.changeStaffMember(target.member.userId, {
              staffRole,
              status: form.status,
              reason: form.reason,
            });
          } else {
            const person = form.person.trim();
            added.current = 'granted';
            try {
              await api.addStaffMember({
                ...(form.findBy === 'email' ? { email: person } : { subject: person }),
                staffRole,
                reason: form.reason,
              });
            } catch (error) {
              // Trace knows nobody with this email yet (they never signed in, and signing in alone creates no one):
              // invite the address instead, with the same role and reason. A sign-in ID cannot be invited, so that
              // refusal is shown as it is.
              if (form.findBy !== 'email' || !isPersonNotFound(error)) throw error;
              await api.inviteStaff({ email: person, staffRole, reason: form.reason });
              added.current = 'invited';
            }
          }
          return undefined;
        }}
        onSaved={() => {
          const role = STAFF_ROLE_LABELS[form.staffRole as StaffRole];
          if (target?.kind === 'revoke') {
            setNotice(`The invitation for ${target.invitation.emailHint} is revoked.`);
            invitationsRead.retry();
            return;
          }
          setNotice(
            target?.kind === 'change'
              ? `${target.member.displayName}: saved.`
              : added.current === 'invited'
                ? `Invitation sent to ${form.person.trim()}. They become ${role} when they accept it.`
                : `${form.person.trim()} is now ${role}.`,
          );
          read.retry();
          if (target?.kind === 'add') invitationsRead.retry();
        }}
      >
        {target?.kind === 'change' || target?.kind === 'revoke' ? null : (
          <>
            <RadioGroup
              legend="Find them by"
              choices={FIND_BY}
              value={form.findBy}
              onChange={(value) => set('findBy', value as Form['findBy'])}
            />
            <TextField
              label={form.findBy === 'email' ? 'Email' : 'Sign-in ID'}
              type={form.findBy === 'email' ? 'email' : 'text'}
              value={form.person}
              onChange={(value) => set('person', value)}
              error={errors.person ?? null}
              spellCheck={false}
              required
            />
          </>
        )}
        {target?.kind === 'revoke' ? null : (
          <SelectField
            label="Role"
            value={form.staffRole}
            onChange={(value) => set('staffRole', value as StaffRole)}
            options={[{ value: '', label: 'Choose a role' }, ...ROLE_OPTIONS]}
            error={errors.staffRole ?? null}
            required
          />
        )}
        {target?.kind === 'change' ? (
          <RadioGroup
            legend="Status"
            choices={STATUS_CHOICES}
            value={form.status}
            onChange={(value) => set('status', value as StaffMember['status'])}
          />
        ) : null}
        <ReasonField
          value={form.reason}
          onChange={(value) => set('reason', value)}
          error={errors.reason ?? null}
        />
      </WriteDialog>
    </AdminPage>
  );
}
